import {
  getProjectSwitches,
  projectAssembleList,
  projectUserAssemble
} from '@/api/project';
import { queryAccountConfig } from '@/api/user';
import { isProd, isPre, systemUpdateKey } from '@/utils/const';
import { ProjectInfo, ModelItem } from './types/app';
import { Action, ActionTree } from 'vuex';
import eventBus from '@/plugins/eventBus';
import { isNil, keyBy, isObject } from 'lodash';
import { startWithEmojiRegex } from '@/regex/emoji';
import config from 'common-module/src/config';
import { RootState } from '@/store/types';
import {
  updateCurrentProjectPermissionWithVuex,
  updateCurrentProjectSwitchWithChangeProject
} from '@/stores/updateWithVuexData';
import { loadAllProjectPermission } from '@/stores/useProjectDataStore';
const ISSUE_IMPORT_FROM = 'issueImportFrom';
const state = {
  currentProject: null,
  currentProjectKey: null,
  currentProjectMembers: [],
  projectList: [] as any[],
  projectRecentVisits: [],
  smartAssistantInit: false,
  canChangeProject: false,
  canChangeProjectCallBack: null as null | (() => Promise<any> | any),
  isGuiding: false,
  issueImportFrom: localStorage.getItem(ISSUE_IMPORT_FROM),
  issueUploading: false,
  commentUploading: false,
  currentVersion:
    isProd || isPre
      ? config.VUE_APP_VERSION
      : Number(localStorage.getItem(systemUpdateKey)) ||
        config.VUE_APP_VERSION,
  versionUpdateInfo: {},
  menuConfig: [] as ModelItem[]
};

type State = typeof state;

const mutations: Record<string, (state: State, ...args: any[]) => void> = {
  SET_ISSUE_UPLOADING(state, boolean) {
    state.issueUploading = boolean;
  },
  SET_COMMENT_UPLOADING(state, boolean) {
    state.commentUploading = boolean;
  },
  SET_ISSUE_IMPORT_FROM(state, str) {
    state.issueImportFrom = str;
    localStorage.setItem(ISSUE_IMPORT_FROM, str);
  },
  SET_IS_GUIDING(state, boolean) {
    state.isGuiding = boolean;
  },
  SET_CURRENTPROJECT(state, pid) {
    state.currentProject = pid;
  },
  SET_PROJECTLIST(state, list) {
    state.projectList = list;
  },
  SET_CURRENTPROJECTKEY(state, pkey) {
    state.currentProjectKey = pkey;
  },
  CLEAR_PROJECTS(state) {
    state.currentProject = null;
    state.currentProjectKey = null;
    state.projectList = [];
  },
  CLEAR_CUR_PROJECT(state) {
    state.currentProject = null;
    state.currentProjectKey = null;
  },
  SET_CURRENT_SMART_INIT(state, isShow) {
    state.smartAssistantInit = isShow;
  },
  setCurrentProjectMember(state, members) {
    state.currentProjectMembers = members || [];
  },
  setProjectRecentVisits(state, visits) {
    state.projectRecentVisits = visits || [];
  },
  SET_CHANGE_PROJECT_STATE(state, { callback, status }) {
    state.canChangeProject = status;
    state.canChangeProjectCallBack = callback;
  },
  CLEAR_CHANGE_PROJECT_STATE(state) {
    state.canChangeProject = false;
    state.canChangeProjectCallBack = null;
  },
  SET_VERSION_UPDATE_INFO(state, version) {
    state.versionUpdateInfo = version;
  },
  UPDATE_VERSION_UPDATE_INFO(state, version) {
    state.versionUpdateInfo = { ...state.versionUpdateInfo, ...version };
  },
  SET_MENU_CONFIG(state, config: ModelItem[]) {
    state.menuConfig = config;
  }
};

const changeProject: Action<State, RootState> = async (
  { commit, dispatch, state, getters },
  { id, force = false, cb }: ProjectInfo
) => {
  const oldProjectId = state.currentProject;
  if (id === oldProjectId && !force) {
    return;
  }
  let project = state.projectList.find(item => item.id === id);
  if (!project) {
    // 要跳转的项目不存在时，先尝试去获取一次最新的项目列表
    // await dispatch('refreshProjectList');
    await dispatch('loadProjects', true);
    project = state.projectList.find(item => item.id === id);
    if (!project) {
      if (state.currentProject) {
        return;
      }

      const defaultProject = state.projectList.find(
        item => item.id === getters.getDefaultProject?.id
      );
      const firstProject = state.projectList[0];
      project = defaultProject || firstProject;
      if (!project) {
        return;
      }
    }
  }
  commit('SET_CURRENTPROJECT', project.id);
  commit('SET_CURRENTPROJECTKEY', project.pkey);
  updateCurrentProjectSwitchWithChangeProject(id);
  cb && cb();
  await Promise.all([
    oldProjectId ? dispatch('refreshProjectList') : Promise.resolve(),
    dispatch('loadProjectMember', { force: true })
  ]);
  eventBus.$emit('global:project-change', project.id, project);
};
const actions: ActionTree<State, RootState> = {
  async updateMenu(ctx) {
    const res = await queryAccountConfig();
    ctx.commit('SET_MENU_CONFIG', res);
  },
  async changeProject(ctx, option: number | ProjectInfo) {
    let id = option;
    if (isObject(option)) {
      // 工作台导入跳转前中断动作
      const asideBeforeProjectChangeCb: Function[] =
        ctx.rootState.user.asideBeforeProjectChangeCb;
      await Promise.all(
        asideBeforeProjectChangeCb
          .filter(cb => typeof cb === 'function')
          .map(cb => cb())
      );

      // 智能助理跳转前中断动作
      const { canChangeProject, canChangeProjectCallBack } = ctx.state;
      if (canChangeProject && typeof canChangeProjectCallBack === 'function') {
        const res = await canChangeProjectCallBack();
        if (!res) {
          return;
        }
      }

      id = option.id;
      const trackInfo = option.trackInfo;
      if (trackInfo) {
        try {
          await eventBus.$track.send('project-click', {
            projectId: trackInfo.id,
            dataId: trackInfo.id,
            dataValue: trackInfo.pname,
            eventGroupCode: trackInfo.eventGroupCode
          });
        } catch (err) {
          console.log(err);
        }
      }
    }
    return changeProject(ctx, {
      id,
      cb: isObject(option) ? option.cb : undefined
    });
  },
  async forceChangeProject(ctx, id) {
    return changeProject(ctx, { id, force: true });
  },
  async refreshProjectList({ dispatch }) {
    return dispatch('loadProjects', true);
  },
  async loadProjects({ state, commit }, force = false) {
    if (force || !state.projectList?.length) {
      const { projects, switches, recentVisits } = await projectAssembleList({
        showArchived: '1',
        queryCollaboratorPro: true
      });
      await loadAllProjectPermission({ refresh: force });
      commit('SET_PROJECTLIST', projects || []);
      commit('features/changeProjectsFeatures', switches || [], { root: true });
      commit('setProjectRecentVisits', recentVisits || []);
    }
  },
  async loadProjectFeatures({ commit, state }, projectId) {
    const currentProject = state.currentProject || projectId;
    const res = await getProjectSwitches(currentProject);
    commit(
      'features/changeCurrentProjectFeatures',
      { ...res, projectId: currentProject },
      { root: true }
    );
    commit('sprint/changeCurrentProjectSprint', currentProject, { root: true });
  },
  async loadProjectMember({ commit, state }, { force = false } = {}) {
    if (
      (force || !state.currentProjectMembers?.length) &&
      state.currentProject
    ) {
      const { users, userRoles, projectRolePermissionVOList } = await projectUserAssemble(
        state.currentProject
      );
      commit('setCurrentProjectMember', users || []);
      commit('user/SET_CURRENT_PROJECT_ROLES', userRoles || [], { root: true });
      updateCurrentProjectPermissionWithVuex(projectRolePermissionVOList || []);
    }
  },
  clearProjects({ commit }) {
    commit('CLEAR_PROJECTS');
  },
  setCanChangeProjectState({ commit }, { callback, status }) {
    commit('SET_CHANGE_PROJECT_STATE', { callback, status });
  },
  clearCanChangeProjectState({ commit }) {
    commit('CLEAR_CHANGE_PROJECT_STATE');
  }
};

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters: {
    // 当前项目是否是示例项目
    currentProjectIsDemo(state) {
      const project = state.projectList.find(
        (i: any) => i.projectType === 'DEMO_PROJECT'
      );
      return project?.id === state.currentProject;
    },
    allMenus(state) {
      return state.menuConfig.flatMap((i: ModelItem) => {
        return [i, ...(i.childList || [])];
      });
    },
    checkMenuVisible: (state, getters) => (key: string) => {
      const item = getters.allMenus.find((i: ModelItem) => i.switchKey === key);
      return item ? item.value === '1' : true;
    },
    currentProject(state, getters) {
      return getters.getProject(state.currentProject);
    },
    projectRecentVisits(state) {
      return state.projectRecentVisits;
    },
    getProject(state) {
      return id => state.projectList.find(f => f.id === id);
    },
    getProjectFirstChar() {
      return name => {
        if (!name) {
          return 'L';
        }
        const emojiResult = name.match(startWithEmojiRegex);
        if (emojiResult) {
          return emojiResult[0];
        }
        return name.slice(0, 1) || 'L';
      };
    },
    currentProjectName(state, getters) {
      return getters.currentProject?.pname;
    },
    currentProjectBgColor(state, getters) {
      return getters.currentProject?.backgroundColor;
    },
    currentProjectFirstChar(state, getters) {
      return getters.getProjectFirstChar(getters.currentProjectName);
    },
    getFirstProject(state) {
      return state.projectList[0] || {};
    },
    getProjectList(state) {
      return state.projectList || [];
    },
    /**
     * 获取默认项目（取最近访问）
     * @param state
     * @param getters
     * @returns {*|null}
     */
    getDefaultProject(state, getters) {
      const projectIdMap = keyBy(getters.getProjectList, 'id');
      const id = state.projectRecentVisits.find(id => !isNil(projectIdMap[id]));
      return id ? projectIdMap[id] : null;
    },
    /**
     * 获取当前或者默认项目ID(忽略已归档)
     * @param state
     * @param getters
     * @returns {*}
     */
    getCurrentOrDefaultProjectId(state, getters) {
      if (getters.currentProject?.id && getters.currentProject.archived !== 1) {
        return getters.currentProject?.id;
      }
      return (
        getters.getProjectList.find(
          item => item.archived !== 1 && item.recentVisit === 1
        )?.id || getters.getProjectList[0]?.id
      );
    },
    isModelShow(state) {
      return (
        state.versionUpdateInfo?.updateType === 2 &&
        state.currentVersion < state.versionUpdateInfo?.totalVersion
      );
    },
    versionClosed(state) {
      return !state.versionUpdateInfo?.show;
    }
  }
};
